/**
 * Lista blanca de esquemas para cualquier URL que cargó un PRODUCTOR.
 *
 * Por qué existe
 * --------------
 * El micrositio y el catálogo son PÚBLICOS, y varios campos de su configuración
 * son texto libre que el productor escribe en el panel. Ninguno se valida al
 * guardar. Si ahí entra un `javascript:...`, se EJECUTA — y no en la máquina de
 * quien lo cargó, sino en la de cada visitante que toque ese link.
 *
 * Campos que hoy dependen de esto (verificado contra el backend):
 *
 * | Campo                                   | Guarda del backend |
 * |-----------------------------------------|--------------------|
 * | `menu_links[].url`                      | ninguna, default `[]` en `DEFAULT_CATALOGO_CONFIG` |
 * | `hero_btn_*_url`, `info_btn_*_url`      | ninguna, default `''`; `set_config()` valida CLAVES, no valores |
 * | `sponsor.url_link`                      | ninguna, `db.String(1024)` y `data.get('url_link')` crudo |
 * | `catalogo_pdf_url`                      | ninguna |
 *
 * Por qué vive ACÁ y no en `menuCatalogo.js`
 * ------------------------------------------
 * Ahí nació, cuando lo único que protegía era el menú del catálogo. Pero apenas
 * empezó a hacer falta para los sponsors y el PDF del REMATE, dejó de ser una
 * pieza del catálogo y pasó a ser una primitiva de seguridad del micrositio
 * entero. La regla de `AGENTS.md` es explícita: *"si un hook colocado empieza a
 * usarse desde otro dominio, se muda a `hooks/`, no se copia"*. Esto es lo
 * mismo con un util — y copiarlo habría sido peor que no tenerlo, porque una
 * copia se endurece y la otra se queda vieja.
 */

// `tel:` y `mailto:` entran porque son destinos legítimos que un productor
// carga a mano. `data:` y `blob:` NO: no hay ningún caso de uso en el panel y
// los dos sirven para ejecutar cosas.
const ESQUEMAS_PERMITIDOS = ['http:', 'https:', 'mailto:', 'tel:'];

// Un esquema al principio del string: `https:`, `mailto:`... y `javascript:`.
// Sirve para SEPARAR los dos casos, no para decidir: lo que decide es la lista
// blanca de arriba.
const CON_ESQUEMA = /^[a-z][a-z0-9+.-]*:/i;

// Un dominio con TLD de letras: `cabanapilaga.com.ar`, `www.pilaga.com`,
// `pilaga.com.ar/genetica`. Es la heurística que separa "el productor escribió
// un sitio y se olvidó el https://" de "el productor escribió cualquier cosa".
const PARECE_DOMINIO = /^[\w-]+(\.[\w-]+)*\.[a-z]{2,}(?=[/:?#]|$)/i;

/**
 * Devuelve la URL si se puede navegar a ella, o `null` si no.
 *
 * @param {string} url  La URL cruda, tal cual la cargó el productor.
 * @returns {string|null}
 */
export const urlSegura = (url) => {
  const limpia = (url || '').trim();
  if (!limpia) return null;

  // Un destino INTERNO se deja tal cual: el navegador lo resuelve contra esta
  // página y es un uso legítimo.
  if (limpia.startsWith('/') || limpia.startsWith('#') || limpia.startsWith('./')) {
    return limpia;
  }

  // SIN ESQUEMA, y acá estaba el agujero. Antes se parseaba todo con
  // `new URL(limpia, window.location.origin)`, así que `www.cabanapilaga.com.ar`
  // —lo más probable que escriba un cabañero en un campo de URL libre—
  // resolvía contra el micrositio, PASABA la lista blanca, y se devolvía tal
  // cual. Después `window.open('www.cabanapilaga.com.ar')` navega RELATIVO al
  // micrositio y cae en un 404. Seguro, pero muerto: y el trabajo de esta
  // función, según su propio docstring, es devolver una URL a la que se PUEDA
  // navegar.
  //
  // Si parece un dominio se le pone el `https://` que el productor se olvidó.
  // Si no —un `asdasd`, un teléfono mal pegado— no hay link, que es mejor que
  // uno que no lleva a ningún lado. Es el criterio que `AGENTS.md` ya fija para
  // el streaming: "sobre una URL suelta se exige además un dominio con TLD,
  // para que un typo tipo `asdasd` no dispare un banner con un botón que no
  // lleva a ningún lado".
  if (!CON_ESQUEMA.test(limpia)) {
    return PARECE_DOMINIO.test(limpia) ? `https://${limpia}` : null;
  }

  try {
    // CON esquema: se parsea con el parser del NAVEGADOR y se pregunta por el
    // protocolo, en vez de comparar el principio del string a mano. Y es a
    // propósito: el parser normaliza antes de resolver, así que un
    // `java\tscript:alert(1)` —con tabs o saltos de línea metidos justamente
    // para burlar un `startsWith('javascript:')`— colapsa a `javascript:` y
    // cae en el filtro.
    const { protocol } = new URL(limpia);
    return ESQUEMAS_PERMITIDOS.includes(protocol) ? limpia : null;
  } catch {
    // URL que no se pudo ni parsear: no se navega a algo que no se entiende.
    return null;
  }
};

/**
 * El `href` de un perfil de red social que cargó el productor.
 *
 * El productor pega indistintamente el usuario (`cabana.pilaga`) o la URL
 * completa copiada del navegador, así que hay que distinguir los dos casos: sin
 * eso salía `https://instagram.com/https://instagram.com/cabana`.
 *
 * Estaba resuelto con `v.startsWith('http') ? v : base + v`, REPETIDO SIETE
 * VECES en `ContactoPage` —las cinco redes del bloque de contacto, más linkedin
 * e instagram de las tarjetas—. Funcionaba, y de hecho bloqueaba un
 * `javascript:` por accidente de estructura: al no empezar con "http" caía en
 * la rama que le antepone el dominio, y el esquema dejaba de estar al
 * principio. Pero era una garantía por casualidad, no por diseño, en siete
 * copias que podían divergir. Acá el que decide es la lista blanca.
 *
 * @param {string} valor  Usuario o URL completa, tal cual lo cargó el productor.
 * @param {string} base   El prefijo del perfil, con la barra: `https://x.com/`.
 * @returns {string|null} `null` si no hay nada que linkear.
 */
export const urlDeRed = (valor, base) => {
  const limpia = (valor || '').trim().replace(/^@/, '');
  if (!limpia) return null;

  // ¿URL PEGADA, O NOMBRE DE USUARIO? Un handle puede tener puntos
  // —`cabana.pilaga` es un usuario de Instagram perfectamente normal— así que
  // "parece un dominio" NO alcanza para decidir: con sólo esa prueba,
  // `cabana.pilaga` terminaba en `https://cabana.pilaga` en vez del perfil.
  //
  // Se exige además una de dos señales de que el productor pegó una URL: que
  // traiga una BARRA —o sea un path, como `instagram.com/cabana.pilaga`— o que
  // empiece con `www.`. Un handle no tiene ninguna de las dos.
  const pegoUnaUrl = CON_ESQUEMA.test(limpia)
    || (PARECE_DOMINIO.test(limpia) && (limpia.includes('/') || /^www\./i.test(limpia)));

  // Si pegó una URL, decide la lista blanca. Si es un usuario, se le cuelga la
  // base — y eso NO necesita lista blanca porque el resultado lo arma este
  // código: el valor del productor queda como un segmento de path, escapado.
  return pegoUnaUrl ? urlSegura(limpia) : `${base}${encodeURIComponent(limpia)}`;
};

/**
 * Abre una URL del productor en otra pestaña, o no hace nada si no es segura.
 *
 * Encapsula las dos cosas que hay que acordarse juntas y que se venían
 * olvidando por separado: la lista blanca, y el `noopener`. Sin `noopener` la
 * pestaña que se abre puede tocar `window.opener` y redirigir ESTA página a
 * donde quiera — un phishing con la sesión del comprador abierta.
 *
 * @param {string} url
 */
export const abrirExterna = (url) => {
  const destino = urlSegura(url);
  if (!destino) return;
  window.open(destino, '_blank', 'noopener,noreferrer');
};
